import React, { useState}  from 'react';
import { connect } from 'react-redux';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import { Card, CardContent, TextField} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { changePass } from 'reducers/UserReducer';
import { Alert, AlertTitle } from '@material-ui/lab';
import { notify } from 'reducers/NotifierReducer';
import { passValidationErrors } from 'utils/functions';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),    
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const useStyles = makeStyles(() => ({ 
  title:{
    background:'#0051a0',
    color: '#fff',    
    minHeight:60,
    marginBottom:10,
    alignContent: 'center', 
    '& h6': {
        fontSize:20,
        fontWeight:400, 
        textAlign: 'left',       
      },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    width: 480,
    minHeight: 150,
    padding:'0px',
  },
  card: {
    width: 480,
    height: 480,
    background: 'fff',
    '& img': {
      width: '100%',
    },
  },
  actions: {
    textAlign: 'center',    
    '& button': {
      background: '#0051a0',
      color: '#fff',
      paddingLeft: '3em',
      paddingRight: '3em',
      textTransform: 'uppercase',
      '&:hover': {
        background: '#021090',
      },
    },
  },
  footer: {
    padding: '16px',
    textAlign: 'center',
    top: 395,
    right: 150,
    position: 'absolute',    
  },
  action: {
    marginBottom: 10,
  },

  form: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 10,
    justifyContent: 'center', 
  },
  w150x250: { 
    maxWidth: 250,    
    minWidth: 450,
  },
  items: {
    justifyContent:'center',
  },
  alert: {
    width:450, 
    marginBottom: 16,
    '& ul': {
      padding: 0,
    },
  },
}));

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

function ChangePasswordDialog(props) {
  const classes = useStyles();
  const { Username } = props;
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmNewPass, setConfirmNewPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);
  const disabled = Boolean(error || !oldPass || !newPass || loading || newPass !== confirmNewPass);
  const repeatPassErr = newPass === confirmNewPass ? false : 'Passwords don\'t match';
  
  React.useEffect(() => {
    const validationErrors = passValidationErrors(Username, newPass);
    if (newPass && validationErrors.length) {
      setError(validationErrors[0]);
    } else {
      if (error) setError(false);
    }
  }, [newPass]);

  const changePassword = async () => {
    setLoading(true);
    try {
      await changePass(oldPass, newPass);
      props.notify('Password updated!', 'success');
      setOldPass('');
      setNewPass('');
      setConfirmNewPass('');
      setErrors([]);
      // props.hidePassExpiration();
    } catch (err) {
      if (err.errors && err.errors.length) {
        setErrors(err.errors);
      } else {
        const message = err.message ? err.message : 'Error updating password';
        setErrors([message]);
      }
    }
    setLoading(false);
  }


  return (
    <Dialog onClose={props.close} open={true}>  
      <DialogTitle id="customized-dialog-title" onClose={props.close} className={classes.title}>
        Change password
      </DialogTitle>
      
      <DialogContent className='p-0'>
      <Card className={classes.card}>
          <CardContent className='p-0'>
            <div className={classes.form}>
                {errors.length > 0 && <Alert severity="error" className={classes.alert}>
                    <AlertTitle>Password validation errors</AlertTitle>
                    <ul>
                    {errors.map((err, idx) => <li key={idx}>{err}</li>)}
                    </ul>
                </Alert>}
                <div className={`${classes.items} ${'py-2'}`}>
                    <TextField 
                    size="small"
                    variant="outlined"
                    className={classes.w150x250}
                    label="Previous Password"
                    placeholder="Enter your previous password"
                    value={oldPass}
                    onChange={(ev) => setOldPass(ev.target.value)}
                    type="password"
                    />
                </div>
                <div className={`${classes.items} ${'py-2'}`}>
                    <TextField
                    size="small"
                    variant="outlined"
                    className={classes.w150x250}
                    label="New Password"
                    value={newPass}
                    placeholder="Enter your new password"
                    onChange={(ev) => setNewPass(ev.target.value)}
                    type="password"
                    error={error}
                    helperText={error ? error : ""}
                    disabled={!oldPass}
                    />
                </div>
                <div className={`${classes.items} ${'py-2'}`}>
                    <TextField
                    size="small"
                    variant="outlined"
                    className={classes.w150x250}
                    label="Repeat New Password"
                    placeholder="Enter your repeat password"
                    value={confirmNewPass}
                    onChange={(ev) => setConfirmNewPass(ev.target.value)}
                    type="password"
                    error={repeatPassErr}
                    helperText={repeatPassErr ? repeatPassErr : ""}
                    disabled={!oldPass}
                    />
                </div>
                <div className={`${classes.items} ${'py-2'}`}>
                    <Button
                    className={classes.w150x250}
                    color="secondary"
                    variant="contained"
                    autoFocus
                    onClick={changePassword}
                    disabled={disabled}>
                    Change Password
                    </Button>
                    {/* {loading && <CircularProgress style={{ marginLeft: 8 }} />} */}
                </div>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

export default connect(null, {notify})(ChangePasswordDialog);
